// ─── playback scheduling ─────────────────────────────────────────────────────
// The arithmetic behind gapless, jitter-tolerant playback of streamed audio
// frames — deliberately separated from Web Audio so it can be tested against a
// fake clock. `Speaker` owns the AudioBufferSourceNodes; this owns *when*.
//
// Two behaviours matter and are easy to get wrong:
//   · a late frame must not overlap the previous one (schedule from a cursor, not
//     from "now"), and
//   · barge-in must drop everything already scheduled but not yet heard.

export interface Slot {
  id: number
  startAt: number
  endAt: number
}

export interface PausedSlot {
  id: number
  /** Seconds already heard from this slot when playback was paused. */
  offsetSec: number
}

export interface PlaybackQueueOptions {
  /**
   * Lead time when starting or restarting a stream. Enough to absorb network
   * jitter without a hearable delay; too much and barge-in feels laggy.
   */
  padSec?: number
  /**
   * How long after the queue empties a new frame still counts as "the same
   * utterance, arriving late" — i.e. a real underrun. Beyond it, the assistant had
   * simply stopped talking and has started again, which is not a fault.
   */
  restartGraceSec?: number
}

const DEFAULT_PAD = 0.06
const DEFAULT_GRACE = 0.25

export class PlaybackQueue {
  private readonly pad: number
  private readonly grace: number
  /** End of the last scheduled frame. 0 = nothing in flight (fresh, or cleared). */
  private cursor = 0
  private nextId = 1
  private slots: Slot[] = []
  /**
   * False once the sender has told us its utterance is over, so the next frame
   * starts a new one rather than continuing a stream that ran dry.
   */
  private continuous = false
  /** Times the stream ran dry mid-utterance — the one number worth watching. */
  underruns = 0

  constructor(options: PlaybackQueueOptions = {}) {
    this.pad = options.padSec ?? DEFAULT_PAD
    this.grace = options.restartGraceSec ?? DEFAULT_GRACE
  }

  /** Reserve the next slot for `durationSec` of audio. Frames stay in order. */
  push(now: number, durationSec: number): Slot {
    this.prune(now)
    let startAt = this.cursor
    if (startAt < now) {
      // Behind the clock. Mid-utterance and only just behind = the stream ran dry,
      // which is a fault worth counting. Otherwise it simply started talking again.
      if (this.continuous && this.cursor > 0 && now - this.cursor <= this.grace) this.underruns++
      startAt = now + this.pad
    }
    this.continuous = true
    const slot = { id: this.nextId++, startAt, endAt: startAt + durationSec }
    this.cursor = slot.endAt
    this.slots.push(slot)
    return slot
  }

  /**
   * The sender says that utterance is finished. Audio already scheduled still plays;
   * the silence that follows is simply silence, not a fault.
   */
  expectRestart(): void {
    this.continuous = false
  }

  /**
   * Barge-in. Returns the ids that have not finished playing, for the caller to
   * stop, and resets the cursor so the next frame starts fresh rather than
   * counting as an underrun.
   */
  clear(now: number): number[] {
    const live = this.slots.filter((s) => s.endAt > now).map((s) => s.id)
    this.slots = []
    this.cursor = 0
    this.continuous = false
    return live
  }

  /**
   * Provisional barge-in. Remove everything that has not finished, but report the
   * exact playhead within the current slot so the caller can resume without
   * repeating audio if the interruption turns out to be noise.
   */
  pause(at: number): PausedSlot[] {
    const live = this.slots
      .filter((slot) => slot.endAt > at)
      .map((slot) => ({
        id: slot.id,
        offsetSec: Math.max(0, at - slot.startAt),
      }))
    this.slots = []
    this.cursor = 0
    this.continuous = false
    return live
  }

  /** Seconds of audio still queued ahead of the clock. */
  buffered(now: number): number {
    return Math.max(0, this.cursor - now)
  }

  playing(now: number): boolean {
    return this.cursor > now
  }

  get pending(): readonly Slot[] {
    return this.slots
  }

  private prune(now: number): void {
    if (this.slots.length && this.slots[0].endAt <= now) {
      this.slots = this.slots.filter((s) => s.endAt > now)
    }
  }
}
